import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import ShareDialog from "@/components/feature/ShareDialog";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import RelatedMovies from "@/pages/detail/components/RelatedMovies";
import { getMovieById, getRelatedMovies, formatViews, formatDate } from "@/utils/movies";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const [shareOpen, setShareOpen] = useState(false);
  const [favorited, setFavorited] = useState(false);

  usePageMeta({
    title: movie ? `热情的邻居 - 《${movie.title}》剧情介绍` : "影视作品不存在 - 热情的邻居",
    description: movie
      ? `《${movie.title}》是一部${movie.region}${movie.type}（${movie.year}），类型为${movie.genres.join("、")}。本文包含完整剧情简介及主要信息介绍。`
      : "您访问的影视作品不存在或已下架。",
    keywords: movie
      ? `${movie.title},热情的邻居,${movie.type},${movie.genres.join(",")},剧情介绍`
      : "热情的邻居,影视内容",
  });

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-10 py-16 md:py-24">
        <div className="max-w-xl mx-auto flex flex-col items-center text-center bg-background-100 border border-background-200/60 rounded-lg py-16 px-6">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-5">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-50">
            影视作品不存在或已下架
          </h1>
          <p className="text-sm text-foreground-500 mt-3 leading-relaxed">
            该影视内容可能已被移除，或链接地址有误。你可以返回首页继续浏览其他影视作品。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors"
            >
              <i className="ri-home-5-line" />
              返回首页
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-5 h-10 rounded-md border border-background-300/60 text-foreground-200 text-sm whitespace-nowrap transition-colors"
            >
              <i className="ri-layout-grid-line" />
              浏览分类
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 5);
  const shareLink = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="w-full px-4 md:px-10 py-8 md:py-12">
      <div className="max-w-7xl mx-auto">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/category" className="hover:text-accent-500 transition-colors">影视分类</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-200 truncate max-w-[160px]">{movie.title}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 md:gap-10">
          <div className="w-full max-w-[280px] mx-auto md:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-200/60 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 影视封面`}
                title={`${movie.title} 影视内容`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-3 left-3 flex items-center gap-1 bg-background-50/85 backdrop-blur px-2.5 py-1 rounded-md">
                <i className="ri-star-fill text-accent-500 text-xs" />
                <span className="text-xs font-semibold text-foreground-50">{movie.rating.toFixed(1)}</span>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs whitespace-nowrap">
                {movie.type}
              </span>
              <span className="px-3 py-1 rounded-full bg-background-200/70 text-foreground-300 text-xs whitespace-nowrap">
                {movie.region}
              </span>
              <span className="px-3 py-1 rounded-full bg-background-200/70 text-foreground-300 text-xs whitespace-nowrap">
                {movie.year}
              </span>
              <span className="px-3 py-1 rounded-full bg-accent-100/70 text-accent-900 text-xs whitespace-nowrap">
                {movie.status}
              </span>
            </div>

            <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-50 leading-tight">
              {movie.title}
            </h1>
            {movie.slogan && (
              <p className="mt-3 text-sm md:text-base text-accent-500 font-heading">{movie.slogan}</p>
            )}

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-sm text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-foreground-500" />
                {formatViews(movie.views)} 次浏览
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-foreground-500" />
                更新于 {formatDate(movie.updateDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-list-check-2 text-foreground-500" />
                {movie.episodes.length > 0 ? `${movie.episodes.length} 集` : "单片"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genre=${encodeURIComponent(genre)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100/70 text-secondary-900 text-xs hover:bg-secondary-500 hover:text-background-50 transition-colors whitespace-nowrap"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-7">
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-share-forward-line text-base" />
                分享
              </button>
              <button
                type="button"
                onClick={() => setFavorited((prev) => !prev)}
                className={`inline-flex items-center gap-2 px-5 h-11 rounded-md border text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  favorited
                    ? "border-accent-500/70 bg-accent-500/15 text-accent-500"
                    : "border-background-300/60 text-foreground-200 hover:bg-background-100"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
            </div>
            {favorited && (
              <p className="text-xs text-foreground-600 mt-3 hn-fade">
                收藏仅为演示功能，暂不会同步到账号。
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 md:gap-12 mt-12">
          <div className="min-w-0 flex flex-col gap-10">
            <section>
              <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50 flex items-center gap-2 mb-4">
                <span className="w-1 h-5 rounded-full bg-primary-500 inline-block" />
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-300 leading-loose">{movie.summary}</p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50 flex items-center gap-2 mb-4">
                <span className="w-1 h-5 rounded-full bg-primary-500 inline-block" />
                详细资料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-background-200/60 border border-background-200/60 rounded-lg overflow-hidden">
                <div className="bg-background-100 p-4">
                  <span className="text-xs text-foreground-500">导演</span>
                  <p className="text-sm text-foreground-100 mt-1.5">{movie.director}</p>
                </div>
                <div className="bg-background-100 p-4">
                  <span className="text-xs text-foreground-500">集数</span>
                  <p className="text-sm text-foreground-100 mt-1.5">
                    {movie.episodes.length > 0 ? `${movie.episodes.length} 集` : "单片电影"}
                  </p>
                </div>
                <div className="bg-background-100 p-4 sm:col-span-2">
                  <span className="text-xs text-foreground-500">主演</span>
                  <p className="text-sm text-foreground-100 mt-1.5">{movie.cast.join("、")}</p>
                </div>
                <div className="bg-background-100 p-4 sm:col-span-2">
                  <span className="text-xs text-foreground-500">分类标签</span>
                  <p className="text-sm text-foreground-100 mt-1.5">
                    {movie.genres.join(" / ")} · {movie.type} · {movie.region} · {movie.year}
                  </p>
                </div>
              </div>
            </section>

            {movie.episodes.length > 0 && (
              <section>
                <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50 flex items-center gap-2 mb-2">
                  <span className="w-1 h-5 rounded-full bg-primary-500 inline-block" />
                  剧集列表
                </h2>
                <p className="text-xs text-foreground-500 mb-4">点击任意一集可展开查看该集剧情简介</p>
                <EpisodeList episodes={movie.episodes} />
              </section>
            )}
          </div>

          <RelatedMovies movies={related} />
        </div>
      </div>

      <ShareDialog
        open={shareOpen}
        link={shareLink}
        title={movie.title}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}